import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, Dictionary } from '@reduxjs/toolkit';
import normalize from 'json-api-normalizer';
import { RootState } from 'Douleutaras/redux/store';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { JSONApiDataObject, ReduxEntity, UserType } from 'Douleutaras/types';
import {
  jobBidAddCustomerOnlinePaymentRelation,
  jobBidRemoveCustomerOnlinePaymentRelation
} from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { responseStatus } from 'Douleutaras/redux/data/auth/helpers';
import { parseV3Errors } from 'Douleutaras/utilities/normalizers/api-errror-normalizer';
import camelCase from 'lodash/camelCase';

export const API_CUSTOMER_ONLINE_PAYMENT_ENDPOINT = 'api/public/v3/customer-online-payment';
export const API_PRO_CUSTOMER_ONLINE_PAYMENT_ENDPOINT = 'api/public/v3/pro-customer-online-payment';
export const MODEL_TYPE = 'customer-online-payments';

const customerOnlinePaymentAdapter = createEntityAdapter<JSONApiDataObject<CustomerOnlinePayment>>();

export enum PaymentStatus {
  IN_PROGRESS = 'in_progress',
  CANCELLED = 'cancelled'
}

type CustomerOnlinePaymentIntentProps = CustomerOnlineGrossPaymentIntentProps | CustomerOnlineNetPaymentIntentProps;

interface CustomerOnlineGrossPaymentIntentProps {
  bidId: string;
  grossAmount: string;
  description?: string;
  promoCode?: string;
}

interface CustomerOnlineNetPaymentIntentProps {
  bidId: string;
  netAmount: string;
  description?: string;
}

export type StripePaymentMethodType = 'card' | 'klarna';

export interface CustomerOnlinePaymentIntentResponse {
  grossAmount: string;
  netAmount: string;
  stripeClientSecret: string;
  stripePaymentIntentId: string;
  stripeAccountId: string;
  vat: string;
  paymentMethodTypes: Array<StripePaymentMethodType>;
  klarnaDisabled: boolean;
  discountAmount: string;
  amountAfterDiscount: string;
}

export interface CustomerOnlinePayment {
  id: number;
  bidId: string;
  jobId: string;
  amount: string;
  description: string;
  netAmount: string;
  buyerFullName: string;
  sellerFullName: string;
  hoursLeftToAccept: string;
  cancelledAutomatically: boolean;
  cancelledByName: string;
  currencyCode: string;
}

export interface CustomerOnlinePaymentStatusChangeProps {
  payId: string;
  bidId: string;
  userType: UserType;
  attributes?: {
    status: PaymentStatus;
    [key: string]: string | boolean | number;
  };
}

export const fetchCustomerOnlinePaymentsAsPro = createAsyncThunk<Dictionary<JSONApiDataObject<CustomerOnlinePayment>>>(
  'customerOnlinePayments/asPro/getAll',
  async (props, api) => {
    const response = await makeRequest(API_PRO_CUSTOMER_ONLINE_PAYMENT_ENDPOINT, {
      method: 'GET',
      additionalGetParams: {
        'filter[status]': PaymentStatus.IN_PROGRESS
      }
    })(api);

    const normalized = normalize(response.data, { endpoint: API_CUSTOMER_ONLINE_PAYMENT_ENDPOINT });

    if (response.status === 200 || response.status === 201) {
      response.data.data.forEach(payment => {
        api.dispatch(jobBidAddCustomerOnlinePaymentRelation({
          bidId: payment.attributes.bidId,
          data: {
            id: payment.id,
            type: camelCase(MODEL_TYPE)
          }
        }));
      });
    }

    return normalized.customerOnlinePayments;
  }
);

export const cancelCustomerOnlinePayment = createAsyncThunk<string, CustomerOnlinePaymentStatusChangeProps>(
  'customerOnlinePayments/status/cancelled',
  async (props, api) => {
    const response = await api.dispatch(customerOnlinePaymentStatusChange({
      ...props,
      attributes: {
        status: PaymentStatus.CANCELLED,
        isCancellationSeen: true
      }
    }));

    // Request was canceled
    if (response.meta.requestStatus === 'fulfilled') {
      await api.dispatch(jobBidRemoveCustomerOnlinePaymentRelation({ bidId: props.bidId }));

      return response.payload as string;
    }

    return api.rejectWithValue(response.payload);
  }
);

export const customerOnlinePaymentStatusChange = createAsyncThunk<string, CustomerOnlinePaymentStatusChangeProps>(
  'customerOnlinePayments/status/changed',
  async (props, api) => {
    const response = await makeRequest(
      `${props.userType === UserType.SELLER
        ? API_PRO_CUSTOMER_ONLINE_PAYMENT_ENDPOINT
        : API_CUSTOMER_ONLINE_PAYMENT_ENDPOINT}/${props.payId}`,
      {
        method: 'PATCH',
        body: {
          data: {
            id: props.payId,
            type: MODEL_TYPE,
            attributes: props.attributes
          }
        }
      })(api);

    const status = responseStatus(response);

    if (status === 200 || status === 201) {
      return response.data.data.id;
    }

    return api.rejectWithValue(parseV3Errors(response.payload.response.data));
  }
);

export const createCustomerOnlinePaymentIntent = createAsyncThunk<CustomerOnlinePaymentIntentResponse, CustomerOnlinePaymentIntentProps>(
  'customerOnlinePaymentIntent/create',
  async (props, api) => {
    const response = await makeRequest(API_CUSTOMER_ONLINE_PAYMENT_ENDPOINT, {
      method: 'POST',
      body: {
        data: {
          type: MODEL_TYPE,
          attributes: props
        }
      }
    })(api);

    if (response.status === 200 || response.status === 201) {
      return response.data.data.attributes;
    }

    throw new Error(response.payload.message);
  }
);

export const markAsSeenCanceledPayments = createAsyncThunk<string, Partial<{ userType: UserType; payId: string }>>(
  'customerOnlinePaymentUnseen/markAsSeenCanceledPayments',
  async (props, api) => {
    const response = await makeRequest(
      `${props.userType === UserType.SELLER
        ? API_PRO_CUSTOMER_ONLINE_PAYMENT_ENDPOINT
        : API_CUSTOMER_ONLINE_PAYMENT_ENDPOINT}/${props.payId}`,
      {
        method: 'PATCH',
        body: {
          data: {
            id: props.payId,
            type: MODEL_TYPE,
            attributes: {
              isCancellationSeen: true
            }
          }
        }
      })(api);

    return response.data.data.id;
  }
);

export const markAsSeenAcceptedPayments = createAsyncThunk<string, Partial<{ payId: string }>>(
  'customerOnlinePaymentUnseen/markAsSeenAcceptedPayments',
  async (props, api) => {
    const response = await makeRequest(
      `${API_CUSTOMER_ONLINE_PAYMENT_ENDPOINT}/${props.payId}`,
      {
        method: 'PATCH',
        body: {
          data: {
            id: props.payId,
            type: MODEL_TYPE,
            attributes: {
              isAcceptanceSeen: true
            }
          }
        }
      })(api);

    return response.data.data.id;
  }
);

export const customerOnlinePaymentSlice = createSlice({
  name: 'customerOnlinePayment',
  initialState: customerOnlinePaymentAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<CustomerOnlinePayment>),
  reducers: {
    customerOnlinePaymentAdded: (state, action) => {
      customerOnlinePaymentAdapter.setMany(state, action.payload);
    },
    customerOnlinePaymentRemoved: (state, action) => {
      customerOnlinePaymentAdapter.removeOne(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchCustomerOnlinePaymentsAsPro.fulfilled, (state, action) => {
      if (action.payload) {
        customerOnlinePaymentAdapter.upsertMany(state, action.payload);
      }
    });
    builder.addCase(markAsSeenCanceledPayments.fulfilled, (state, action) => {
      customerOnlinePaymentAdapter.removeOne(state, action.payload);
    });
    builder.addCase(markAsSeenAcceptedPayments.fulfilled, (state, action) => {
      customerOnlinePaymentAdapter.removeOne(state, action.payload);
    });
    builder.addCase(cancelCustomerOnlinePayment.fulfilled, (state, action) => {
      customerOnlinePaymentAdapter.removeOne(state, action.payload);
    });
  }
});

export const { customerOnlinePaymentAdded, customerOnlinePaymentRemoved } = customerOnlinePaymentSlice.actions;
// tslint:disable:max-line-length
export const customerOnlinePaymentSelectors = customerOnlinePaymentAdapter.getSelectors((state: RootState) => state.payments.customerOnlinePayment);

export const selectAllPaymentsByBidIds = createSelector([
  state => state.jobBids,
  state => state.payments.customerOnlinePayment,
  (state, { bidIds }) => ({ bidIds } as { bidIds: Array<string> })
], (bids, requests, { bidIds }) => {
  const results = [];

  bidIds.forEach(bidId => {
    const bid = bids.entities[bidId];

    if (!bid.relationships.customerOnlinePayment?.data?.id) {
      return;
    }

    const payment = requests.entities[bid.relationships.customerOnlinePayment?.data.id];

    if (payment) {
      results.push(payment);
    }
  });

  return results;
});

export const selectAllPaymentsByIds = createSelector([
  state => state.payments.customerOnlinePayment,
  (state, { ids }) => ({ ids } as { ids: Array<string> })
], (payments, { ids }) => {
  const results = [];

  ids.forEach(id => {
    const payment = payments.entities[id];

    if (!payment) {
      return;
    }

    results.push(payment);
  });

  return results;
});
